import { useState, useEffect, useCallback } from "react";
import { audioProcessingAPI } from "../../lib/api";
import type {
  AudioProcessingStepType,
  AudioProcessingJobSummary,
  AudioProcessingStatus,
} from "../../types/api";
import styles from "./AudioProcessing.module.css";

const PROCESSING_STEPS: { value: AudioProcessingStepType; label: string }[] = [
  { value: "silence_removal", label: "Silence Removal" },
  { value: "normalization", label: "Normalization" },
  { value: "noise_reduction", label: "Noise Reduction" },
  { value: "transcription", label: "Transcription" },
];

interface AudioProcessingProps {
  organizationId: string;
  podcastId: string;
  episodeId: string;
}

function statusLabel(status: AudioProcessingStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "processing":
      return "Processing…";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
  }
}

export default function AudioProcessing({
  organizationId,
  podcastId,
  episodeId,
}: AudioProcessingProps) {
  const [selectedSteps, setSelectedSteps] = useState<Set<AudioProcessingStepType>>(new Set());
  const [jobs, setJobs] = useState<AudioProcessingJobSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasActiveJob = jobs.some((j) => j.status === "pending" || j.status === "processing");

  const fetchJobs = useCallback(async () => {
    try {
      const result = await audioProcessingAPI.list(organizationId, podcastId, episodeId);
      setJobs(result.jobs);
    } catch {
      // Silently fail on poll
    }
  }, [organizationId, podcastId, episodeId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!hasActiveJob) return;
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [hasActiveJob, fetchJobs]);

  const toggleStep = (step: AudioProcessingStepType) => {
    setSelectedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedSteps.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await audioProcessingAPI.start(
        organizationId,
        podcastId,
        episodeId,
        Array.from(selectedSteps),
      );
      setSelectedSteps(new Set());
      await fetchJobs();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start processing");
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = hasActiveJob || submitting;

  return (
    <div className={styles.container}>
      <h3>Audio Processing</h3>

      <div className={styles.stepList}>
        {PROCESSING_STEPS.map((step) => (
          <label key={step.value} className={styles.stepCheckbox}>
            <input
              type="checkbox"
              checked={selectedSteps.has(step.value)}
              onChange={() => toggleStep(step.value)}
              disabled={disabled}
            />
            <span>{step.label}</span>
          </label>
        ))}
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <button
        type="button"
        className={styles.processButton}
        onClick={handleSubmit}
        disabled={disabled || selectedSteps.size === 0}
      >
        {submitting ? "Starting…" : hasActiveJob ? "Processing in progress…" : "Process Audio"}
      </button>

      {jobs.length > 0 && (
        <div className={styles.jobList}>
          <h4>Processing History</h4>
          <ul>
            {jobs.map((job) => (
              <li key={job.jobId} className={styles.jobItem}>
                <span className={`${styles.statusBadge} ${styles[job.status]}`}>
                  {statusLabel(job.status)}
                </span>
                <span className={styles.jobSteps}>{job.steps.join(", ")}</span>
                <time className={styles.jobTime}>{new Date(job.createdAt).toLocaleString()}</time>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
